import { Action } from '../types/types';
import {
	SET_SHOWBOARD_EXPANDED,
	SET_LIST_SORT_TYPE,
	SET_LIST_SHOW_PAST,
	SET_LIST_SHOW_ALL,
	SET_SHOW_CALENDAR,
	SET_SELECTED_CALENDAR_VIEW,
	SET_SELECTED_CALENDAR_OPTION,
	SET_MEDIA_FILTERS,
	SET_OPEN_CONTEXT_MENU_ID,
	SET_DRAGGING_PROPS,
	SET_STYLE_PACK_TYPE_FILTER,
	SHOW_DEPRECATION_WARNING,
} from '../actions/user-interface';
import { SlidingMenuOption } from '../../components/general-components/sliding-menu';
import { MEDIA_FILTER_TYPES, MEDIA_SORT_TYPES } from '../../types/constants';

export const calendarOptions = [
	{ label: 'Month', value: 'dayGridMonth' },
	{ label: 'Week', value: 'timeGridWeek' },
	{ label: 'Day', value: 'timeGridDay' },
];

export const DRAG_TYPE = {
	OVERLAY: 'overlay',
	FEED_MEDIA: 'media',
	FEED_MEDIA_AUDIO: 'audio',
	FEED_PRESENTER: 'presenter',
	FEED_EMPTY: 'empty',
	FEED_EMAIL: 'email',
	FEED_SCREENSHARE: 'screenshare',
	TITLE: 'title',
	// for easier syntax passing into drop area accepted types
	FEEDS: [
		'media',
		'presenter',
		'email',
		'screenshare',
		'title',
	],
};

export interface DragItem {
	id: number | string,
	type: string, // corresponds to DRAG_TYPE
	name?: string | null,
	template?: string | null,
	provider?: string | null,
}
export interface RelativePosition {
	x: number,
	y: number,
}
export interface DropAreaData {
	feedPosition?: number
	id?: string // id of drop element
	relativePosition?: RelativePosition
}

export interface DropCallbackData extends DropAreaData {
	relativePosition: RelativePosition
}

export interface DropCallback {
	(item: DragItem, dropData: DropCallbackData): void
}

export interface UserInterfaceState {
	isShowboardExpanded: boolean;
	listSortType: string;
	listShowPast: boolean;
	listShowAll: boolean;
	showCalendar: boolean;
	selectedCalendarView: string;
	selectedCalendarOption: SlidingMenuOption | null;
	mediaFilters: MediaFilters;
	openContextMenuId: string | null;
	globalIsDragging: string | null; // is feed or overlay being dragged
	idOfDragging: string | number | null; // id of entity, or string- email of presenter/screenshare
	typeOfDragging: string | null; // DRAG_TYPE of item being dragged
	stylePackTypeFilter: string | undefined;
	showDeprecationWarning: boolean;
}

export interface MediaFilters {
	selectedFilterType: string;
	selectedSortType: string;
}

export const initialUserInterfaceState: UserInterfaceState = {
	isShowboardExpanded: false,
	listSortType: 'none',
	listShowPast: false,
	listShowAll: false,
	showCalendar: false,
	selectedCalendarView: calendarOptions[0].value,
	selectedCalendarOption: null,
	mediaFilters: {
		selectedFilterType: MEDIA_FILTER_TYPES.ALL,
		selectedSortType: MEDIA_SORT_TYPES.NONE,
	},
	openContextMenuId: null,
	globalIsDragging: null,
	idOfDragging: null,
	typeOfDragging: null,
	stylePackTypeFilter: undefined,
	showDeprecationWarning: false,
};

const actions: Map<any, Function> = new Map([
	[
		SET_SHOWBOARD_EXPANDED,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			isShowboardExpanded: action.payload,
		}),
	],
	[
		SET_LIST_SORT_TYPE,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			listSortType: action.payload,
		}),
	],
	[
		SET_LIST_SHOW_PAST,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			listShowPast: action.payload,
		}),
	],
	[
		SET_LIST_SHOW_ALL,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			listShowAll: action.payload,
		}),
	],
	[
		SET_SHOW_CALENDAR,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			showCalendar: action.payload,
		}),
	],
	[
		SET_SELECTED_CALENDAR_VIEW,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			selectedCalendarView: action.payload,
		}),
	],
	[
		SET_SELECTED_CALENDAR_OPTION,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			selectedCalendarOption: action.payload,
		}),
	],
	[
		SET_MEDIA_FILTERS,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			mediaFilters: action.payload,
		}),
	],
	[
		SET_OPEN_CONTEXT_MENU_ID,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			openContextMenuId: action.payload,
		}),
	],
	[
		SET_DRAGGING_PROPS,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			...action.payload,
		}),
	],
	[
		SET_STYLE_PACK_TYPE_FILTER,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			stylePackTypeFilter: action.payload,
		}),
	],
	[
		SHOW_DEPRECATION_WARNING,
		(state: UserInterfaceState, action: Action): UserInterfaceState => ({
			...state,
			showDeprecationWarning: action.payload,
		}),
	],
]);

export function UserInterfaceReducer(
	// eslint-disable-next-line default-param-last
	state: UserInterfaceState = initialUserInterfaceState,
	action: Action,
) {
	return actions.get(action.type)?.(state, action) || state;
}
